import { FormEvent } from 'react';

interface Step1Props {
  data: Record<string, string>;
  onChange: (field: string, value: string) => void;
  onNext: () => void;
}

const ROLES = [
  { value: 'Property owner', icon: 'ri-home-heart-line', desc: 'I own this villa' },
  { value: 'Managing agent', icon: 'ri-briefcase-line', desc: 'I manage it on behalf of the owner' },
  { value: 'Property manager', icon: 'ri-user-settings-line', desc: 'Day-to-day operations' },
];

export default function Step1Details({ data, onChange, onNext }: Step1Props) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!data.firstName || !data.lastName || !data.email || !data.role) return;
    onNext();
  };

  return (
    <div className="w-full max-w-xl">
      <p className="text-[#b6862d] text-xs tracking-widest uppercase font-sans mb-5">Step 1 of 6</p>
      <h1 className="font-serif text-4xl md:text-5xl text-[#141413] mb-3 leading-tight">
        Let&apos;s start with you.
      </h1>
      <p className="text-[#77776f] text-sm font-sans leading-relaxed mb-10">
        Purely for our records — never shown on your listing.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">First Name</label>
            <input
              type="text"
              name="firstName"
              required
              value={data.firstName || ''}
              onChange={(e) => onChange('firstName', e.target.value)}
              className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
              placeholder="James"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Last Name</label>
            <input
              type="text"
              name="lastName"
              required
              value={data.lastName || ''}
              onChange={(e) => onChange('lastName', e.target.value)}
              className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
              placeholder="Hartley"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Email Address</label>
          <input
            type="email"
            name="email"
            required
            value={data.email || ''}
            onChange={(e) => onChange('email', e.target.value)}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
            placeholder="you@example.com"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans flex items-center gap-2">
            Phone Number
            <span className="text-[#77776f] text-[10px] normal-case tracking-normal font-sans">optional</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={data.phone || ''}
            onChange={(e) => onChange('phone', e.target.value)}
            className="bg-white border border-[#e0d9ce] rounded-lg px-4 py-3 text-sm text-[#141413] font-sans outline-none focus:border-[#b6862d] transition-colors"
            placeholder="+34 600 000 000"
          />
        </div>

        <div className="flex flex-col gap-2.5">
          <label className="text-[#3a3a34] text-xs tracking-widest uppercase font-sans">Your Role</label>
          <div className="flex flex-col gap-2">
            {ROLES.map((role) => (
              <button
                key={role.value}
                type="button"
                onClick={() => onChange('role', role.value)}
                className={`flex items-center gap-4 px-5 py-4 rounded-xl text-left border-2 transition-all duration-200 cursor-pointer ${
                  data.role === role.value
                    ? 'bg-[#234012] border-[#234012] text-[#fcfbf7]'
                    : 'bg-white border-[#e0d9ce] text-[#3a3a34] hover:border-[#b6862d]'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${
                  data.role === role.value ? 'bg-[#305a18]' : 'bg-[#f2ece2]'
                }`}>
                  <i className={`${role.icon} text-base ${data.role === role.value ? 'text-[#fcfbf7]' : 'text-[#b6862d]'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium font-sans">{role.value}</p>
                  <p className={`text-xs font-sans mt-0.5 ${data.role === role.value ? 'text-[#fcfbf7]/70' : 'text-[#77776f]'}`}>
                    {role.desc}
                  </p>
                </div>
                <div className={`ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  data.role === role.value ? 'bg-[#b6862d] border-[#b6862d]' : 'border-[#e0d9ce]'
                }`}>
                  {data.role === role.value && <i className="ri-check-line text-[10px] text-white" />}
                </div>
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={!data.firstName || !data.lastName || !data.email || !data.role}
          className="mt-4 bg-[#234012] text-[#fcfbf7] text-sm font-sans font-medium px-8 py-4 rounded-lg hover:bg-[#305a18] transition-colors duration-200 cursor-pointer whitespace-nowrap w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </form>
    </div>
  );
}
